import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';

import { Link } from '@mui/material';

import { useMemo } from 'react';

import {
  ErrorMessage,
  Form,
  TextField,
  Button,
  Typography,
} from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';

import { ClassValidatorResolver } from '@app-velocity/validators';

import { useRequestGateway } from '@app-velocity/request-gateway';

import { useGetConfig } from '@app-velocity/config';

import {
  AuthRoutesPaths,
  useUserAuthentication,
} from '@app-velocity/libs/auth';

import { UserLoginDto } from '../../dto/UserLoginDto';
import { AuthOptions } from './components/AuthOptions';
import { useAuthProviderContext } from '../../context/useAuthProviderContext';

export const LoginForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UserLoginDto>({
    mode: 'onSubmit',
    resolver: ClassValidatorResolver(UserLoginDto),
  });

  const { type: authType, setFormType } = useAuthProviderContext();

  const { data: config } = useGetConfig();

  const { t } = useI18n();

  const { source } = useRequestGateway();

  const showSkipButton = useMemo(() => {
    if (source !== 'figma') return false;
    if (!config || config.config?.guestEnabled === false) return false;

    return true;
  }, [config, source]);

  const {
    mutate: authenticateUser,
    error,
    isPending,
  } = useUserAuthentication();

  const onSubmit = (data: UserLoginDto) => {
    authenticateUser(data);
  };

  const switchToRegister = () => {
    setFormType('register');
  };

  const goToForgotPassword = () => {
    window.open(AuthRoutesPaths.ForgotPassword, '_blank');
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} data-testid="form-login">
      <Form.FieldContainer>
        <Form.Label>{t('users:email')}</Form.Label>
        <TextField
          placeholder={t('users:auth.email_placeholder')}
          fullWidth
          control={control}
          name="email"
          size="large"
          helperText={errors.email?.message}
          error={!!errors.email}
          inputProps={{
            'data-testid': 'form-login-email',
            'data-ph-capture-attribute-id': 'user_auth_email',
            autoComplete: 'email',
          }}
        />
      </Form.FieldContainer>
      <Form.FieldContainer>
        <Form.LabelContainer>
          <Form.Label>{t('users:password')}</Form.Label>
          <Form.SideLabel>
            {authType === 'detached' ? (
              <Button variant="link" onClick={goToForgotPassword}>
                {t('users:forgot_password.title')}
              </Button>
            ) : (
              <Link component={RouterLink} to={AuthRoutesPaths.ForgotPassword}>
                <Typography>{t('users:forgot_password.title')}</Typography>
              </Link>
            )}
          </Form.SideLabel>
        </Form.LabelContainer>
        <TextField
          placeholder={t('users:auth.password_placeholder')}
          fullWidth
          name="password"
          size="large"
          control={control}
          type="password"
          helperText={errors.password?.message}
          error={!!errors.password}
          inputProps={{
            'data-testid': 'form-login-password',
            'data-ph-capture-attribute-id': 'user_auth_password',
          }}
        />
      </Form.FieldContainer>

      <ErrorMessage error={error} />

      <Form.ButtonsContainer>
        <Button
          fullWidth
          disabled={isPending}
          type="submit"
          variant="contained"
          data-testid="form-login-submit"
          data-ph-capture-attribute-id="user_auth_submit_button"
          size="large"
        >
          {t('users:auth.sign_in')}
        </Button>
        {showSkipButton ? (
          <Button
            component={RouterLink}
            fullWidth
            disabled={isPending}
            type="submit"
            variant="basic"
            data-testid="form-login-skip"
            data-ph-capture-attribute-id="user_auth_skip_login_button"
            size="large"
            to={AuthRoutesPaths.Guest}
          >
            {t('users:auth.skip_for_now')}
          </Button>
        ) : null}

        <AuthOptions />

        <Form.SecondaryText>
          {t('users:auth.do_not_have_account_yet')}{' '}
          {authType === 'detached' ? (
            <Button variant="link" onClick={switchToRegister}>
              {t('users:auth.sign_up')}
            </Button>
          ) : (
            <Link component={RouterLink} to={AuthRoutesPaths.Registration}>
              {t('users:auth.sign_up')}
            </Link>
          )}
        </Form.SecondaryText>
      </Form.ButtonsContainer>
    </Form>
  );
};
